// §4.13 — SALES SETTINGS, /sales/settings (Module 09, P3-E PR-E2, D-292
// ruling 11). The §2.22 row as a screen.
//
// WHY A SCREEN. The reminder engine reads these knobs every minute — the two
// reminder sequences and their per-tier overrides, quiet hours, the automatic
// delivery confirmation — and until this screen they were reachable only by
// `curl`. A setting a factory admin cannot change is not finishable by a
// factory admin (doctrine 2). The screen also states when the automatic checks
// last ran (`engine_swept_at`): that line is the deployed proof the worker
// runs at all, and the first thing to look at when a reminder did not go.
//
// WHO READS, WHO EDITS. The GET rides `sales.read`, and so does the screen:
// §4.13 gives it to everyone with `sales.read` (sales, planner, finance, qc,
// the storekeeper and the admins — the knobs decide what their customers are
// told and when a delivery confirms itself). The PUT rides `sales.tier.manage`
// (ga/fa); everyone else reads the same values in read-only controls, with a
// sentence saying who changes them — never a Save that answers 403.
//
// THE PUT IS FULL-ROW (§5) and strict. The body is built field by field from
// the form (`settingsPutBody`): the two read-only stamps on the row would be
// refused, and the three document-text bundles (D-288) are left OUT, which
// keeps them as stored — this screen does not edit them, and a PUT that sent
// what it read would overwrite a concurrent edit of them with a stale copy.
//
// THE SERVER'S VALIDATION LANDS ON THE FIELD. A 400's `errors[].path` names
// the body key (`ready_reminder_max_sends`, `reminder_tier_overrides.b2b.
// quote_follow_up_after_days`), and each is rendered under the control it
// names, in the field's own sentence. The client checks the same bounds first
// so the common case never makes the round trip.
//
// THE TAGS CARD (P3-E2 PR E2-2a1, D-323 ruling 5) sits last, with its own
// reads and writes (components/tags/tags-card.tsx), as the Pipeline stages
// card does: curating the tag list never goes through the settings row's PUT.
//
// A FAILED READ IS AN ERROR CARD, never a blank. During a deploy roll an old
// task can answer this route's older shape or a 404 for a moment (plan risk
// 5); the card names what failed, carries the request id and offers Retry.

import { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesGroupTabs } from "../components/group-tabs";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { HelpTip } from "../../../../components/help-tip";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CheckboxField, Field } from "../../../../components/field";
import { CardSkeleton, ErrorState, ForbiddenState } from "../../../../components/states";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf, type FieldError } from "../../../../lib/request";
import { useTenantTimezone } from "../../../../lib/role-codes";
import { MutationFailureNote } from "../mutation-failure-note";
import { PipelineStagesCard } from "../components/pipeline/stages-card";
import { TagsCard } from "../components/tags/tags-card";
import {
  canOpenSalesSettings,
  isSalesReadRefused,
  useSalesPermissions,
} from "../hooks/permissions";
import {
  useSalesSettings,
  useUpdateSalesSettings,
  type SalesSettings,
  type SalesSettingsPutRequest,
} from "../hooks/sales-settings-queries";

// ---------------------------------------------------------------------------
// the form model (pure, unit-tested)
// ---------------------------------------------------------------------------

/** The four cadence keys — the ONLY keys a tier override may carry (§2.22;
 * the contract refuses any other). */
export const CADENCE_KEYS = [
  "ready_reminder_after_days",
  "ready_reminder_max_sends",
  "quote_follow_up_after_days",
  "quote_follow_up_max_sends",
] as const;
export type CadenceKey = (typeof CADENCE_KEYS)[number];

/** The price tiers an override row exists for — the contract's own enum. */
export const TIERS = ["market", "b2b", "distributor"] as const;
export type Tier = (typeof TIERS)[number];

type RangeKey = CadenceKey | "delivery_auto_confirm_days";

/** The tier table's column header for each cadence key. */
const CADENCE_COLUMN_KEYS: Readonly<Record<CadenceKey, string>> = {
  ready_reminder_after_days: "sales.settings.colReadyAfter",
  ready_reminder_max_sends: "sales.settings.colReadyMax",
  quote_follow_up_after_days: "sales.settings.colFollowAfter",
  quote_follow_up_max_sends: "sales.settings.colFollowMax",
};

/** Each number's bounds — the contract's, restated so the client refuses what
 * the server would. */
export const SETTING_BOUNDS: Readonly<Record<RangeKey, readonly [number, number]>> = {
  ready_reminder_after_days: [1, 30],
  ready_reminder_max_sends: [0, 10],
  quote_follow_up_after_days: [1, 30],
  quote_follow_up_max_sends: [0, 5],
  delivery_auto_confirm_days: [1, 60],
};

/** The form as typed: numbers as text (a cleared box is a state, not a 0),
 * and a tier cell's "" meaning "use the default". */
export interface SettingsForm {
  readonly ready_reminder_after_days: string;
  readonly ready_reminder_max_sends: string;
  readonly quote_follow_up_after_days: string;
  readonly quote_follow_up_max_sends: string;
  readonly tiers: Readonly<Record<Tier, Readonly<Record<CadenceKey, string>>>>;
  readonly quiet_hours_start: string;
  readonly quiet_hours_end: string;
  readonly delivery_auto_confirm_days: string;
  readonly tracking_show_prices: boolean;
  readonly delivery_note_show_prices: boolean;
}

export function formFromSettings(row: SalesSettings): SettingsForm {
  const tier = (name: Tier): Record<CadenceKey, string> => {
    const override = row.reminder_tier_overrides[name] ?? {};
    return {
      ready_reminder_after_days: override.ready_reminder_after_days?.toString() ?? "",
      ready_reminder_max_sends: override.ready_reminder_max_sends?.toString() ?? "",
      quote_follow_up_after_days: override.quote_follow_up_after_days?.toString() ?? "",
      quote_follow_up_max_sends: override.quote_follow_up_max_sends?.toString() ?? "",
    };
  };
  return {
    ready_reminder_after_days: String(row.ready_reminder_after_days),
    ready_reminder_max_sends: String(row.ready_reminder_max_sends),
    quote_follow_up_after_days: String(row.quote_follow_up_after_days),
    quote_follow_up_max_sends: String(row.quote_follow_up_max_sends),
    tiers: { market: tier("market"), b2b: tier("b2b"), distributor: tier("distributor") },
    quiet_hours_start: row.quiet_hours_start,
    quiet_hours_end: row.quiet_hours_end,
    delivery_auto_confirm_days: String(row.delivery_auto_confirm_days),
    tracking_show_prices: row.tracking_show_prices,
    delivery_note_show_prices: row.delivery_note_show_prices,
  };
}

/** A field's message: an i18n key and its parameters. */
export interface FieldMessage {
  readonly key: string;
  readonly params?: Readonly<Record<string, number>>;
}

function rangeMessage(key: RangeKey): FieldMessage {
  const [min, max] = SETTING_BOUNDS[key];
  return { key: "sales.settings.range", params: { min, max } };
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/u;

function inRange(text: string, key: RangeKey): boolean {
  if (!/^\d+$/u.test(text.trim())) return false;
  const value = Number(text.trim());
  const [min, max] = SETTING_BOUNDS[key];
  return value >= min && value <= max;
}

/** The field path a message belongs to — the SAME spelling the server's
 * `errors[].path` uses, so client and server errors land on one control. */
export function tierPath(tier: Tier, key: CadenceKey): string {
  return `reminder_tier_overrides.${tier}.${key}`;
}

/** Client-side checks against the contract's bounds (pure). */
export function validateSettingsForm(form: SettingsForm): Record<string, FieldMessage> {
  const errors: Record<string, FieldMessage> = {};
  for (const key of [...CADENCE_KEYS, "delivery_auto_confirm_days"] as const) {
    if (!inRange(form[key], key)) errors[key] = rangeMessage(key);
  }
  for (const tier of TIERS) {
    for (const key of CADENCE_KEYS) {
      const text = form.tiers[tier][key];
      if (text.trim().length > 0 && !inRange(text, key))
        errors[tierPath(tier, key)] = rangeMessage(key);
    }
  }
  for (const key of ["quiet_hours_start", "quiet_hours_end"] as const) {
    if (!TIME.test(form[key])) errors[key] = { key: "sales.settings.time" };
  }
  return errors;
}

/** The server's 400 `errors[]`, keyed by the path the form renders under.
 * Every number field's message is its own range sentence, whatever zod's
 * code said (`too_small`, `too_big`, `invalid_type` — the reader's remedy is
 * the same). A path the form has no control for is dropped here and the
 * screen's general refusal note carries the save failure instead. */
export function serverSettingsErrors(
  fieldErrors: readonly FieldError[],
): Record<string, FieldMessage> {
  const errors: Record<string, FieldMessage> = {};
  for (const fe of fieldErrors) {
    const path = fe.path;
    const last = path.split(".").pop() ?? path;
    if (path === "quiet_hours_start" || path === "quiet_hours_end") {
      errors[path] = { key: "sales.settings.time" };
    } else if (last in SETTING_BOUNDS) {
      errors[path] = rangeMessage(last as RangeKey);
    }
  }
  return errors;
}

/** The PUT body, built field by field (the header says why never spread). A
 * tier carries only the cells that were filled; a tier with none is omitted. */
export function settingsPutBody(form: SettingsForm): SalesSettingsPutRequest {
  const overrides: SalesSettingsPutRequest["reminder_tier_overrides"] = {};
  for (const tier of TIERS) {
    const cells: Partial<Record<CadenceKey, number>> = {};
    for (const key of CADENCE_KEYS) {
      const text = form.tiers[tier][key].trim();
      if (text.length > 0) cells[key] = Number(text);
    }
    if (Object.keys(cells).length > 0) overrides[tier] = cells;
  }
  return {
    ready_reminder_after_days: Number(form.ready_reminder_after_days.trim()),
    ready_reminder_max_sends: Number(form.ready_reminder_max_sends.trim()),
    quote_follow_up_after_days: Number(form.quote_follow_up_after_days.trim()),
    quote_follow_up_max_sends: Number(form.quote_follow_up_max_sends.trim()),
    reminder_tier_overrides: overrides,
    quiet_hours_start: form.quiet_hours_start,
    quiet_hours_end: form.quiet_hours_end,
    delivery_auto_confirm_days: Number(form.delivery_auto_confirm_days.trim()),
    tracking_show_prices: form.tracking_show_prices,
    delivery_note_show_prices: form.delivery_note_show_prices,
  };
}

/** "3 min ago" for the engine line — minutes, then hours, then days (the
 * counted-noun-free forms D-030 asks for). A stamp in the future (a clock
 * skew between the reader and the server) reads as "just now", never as a
 * negative age. */
export function engineRanAgo(t: TFunction, iso: string, nowMs: number): string {
  const stamp = Date.parse(iso);
  const minutes = Number.isNaN(stamp) ? 0 : Math.floor((nowMs - stamp) / 60_000);
  if (minutes < 1) return t("sales.settings.agoNow");
  if (minutes < 60) return t("sales.settings.agoMinutes", { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return t("sales.settings.agoHours", { n: hours });
  return t("sales.settings.agoDays", { n: Math.floor(hours / 24) });
}

// ---------------------------------------------------------------------------
// the screen
// ---------------------------------------------------------------------------

export function SalesSettingsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const timeZone = useTenantTimezone();

  const refused = isSalesReadRefused(perms, canOpenSalesSettings(perms));
  const canEdit = perms.has("sales.tier.manage");
  const settings = useSalesSettings(!refused);
  const save = useUpdateSalesSettings();

  const [form, setForm] = useState<SettingsForm | null>(null);
  const [dirty, setDirty] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saved, setSaved] = useState(false);

  // Seed the form from the row — and re-seed from every newer read while the
  // reader has not typed (the row moves every minute: the engine stamps it).
  useEffect(() => {
    if (settings.data !== undefined && !dirty) setForm(formFromSettings(settings.data));
  }, [settings.data, dirty]);

  useEffect(() => {
    if (settings.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [settings.error, navigate]);

  if (refused) {
    return (
      <section className="sales-settings-screen" data-testid="sales-settings-screen">
        <PageHeader title={t("sales.settings.title")} />
        <SalesGroupTabs group="settings" />
        <ForbiddenState title={t("sales.access.title")} body={t("sales.access.settings")} />
      </section>
    );
  }

  const row = settings.data;
  if (row === undefined || form === null) {
    return (
      <section className="sales-settings-screen" data-testid="sales-settings-screen">
        <PageHeader title={t("sales.settings.title")} />
        <SalesGroupTabs group="settings" />
        {settings.error !== null && !(settings.error instanceof UnauthorizedError) ? (
          <ErrorState
            title={t("sales.settings.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(settings.error)}
            onRetry={() => {
              void settings.refetch();
            }}
          />
        ) : (
          <div data-testid="sales-settings-loading">
            <CardSkeleton lines={8} label={t("sales.settings.loading")} />
          </div>
        )}
      </section>
    );
  }

  const clientErrors = submitted ? validateSettingsForm(form) : {};
  const serverErrors =
    save.error instanceof RequestError && save.error.httpStatus === 400
      ? serverSettingsErrors(save.error.fieldErrors)
      : {};
  const errorFor = (path: string): FieldMessage | undefined =>
    clientErrors[path] ?? serverErrors[path];
  const hasFieldErrors = Object.keys(clientErrors).length + Object.keys(serverErrors).length > 0;

  const edit = (next: SettingsForm) => {
    setForm(next);
    setDirty(true);
    setSaved(false);
    save.reset();
  };

  const onSave = () => {
    setSubmitted(true);
    setSaved(false);
    if (Object.keys(validateSettingsForm(form)).length > 0) return;
    save.mutate(settingsPutBody(form), {
      onSuccess: (stored) => {
        setForm(formFromSettings(stored));
        setDirty(false);
        setSubmitted(false);
        setSaved(true);
      },
    });
  };

  const errorLine = (path: string) => {
    const message = errorFor(path);
    return message === undefined ? null : (
      <span
        className="field-error"
        role="alert"
        data-testid="sales-settings-field-error"
        data-path={path}
      >
        {t(message.key, message.params ?? {})}
      </span>
    );
  };

  /** One number field, bound to a top-level key. */
  const numberField = (key: RangeKey, label: string) => (
    <Field label={label}>
      <input
        inputMode="numeric"
        value={form[key]}
        readOnly={!canEdit}
        aria-invalid={errorFor(key) !== undefined ? true : undefined}
        data-testid={`sales-settings-${key}`}
        onChange={(e) => {
          edit({ ...form, [key]: e.target.value });
        }}
      />
      {errorLine(key)}
    </Field>
  );

  const tierColumns: DataTableColumn<Tier>[] = [
    {
      key: "tier",
      header: t("sales.settings.colTier"),
      id: true,
      render: (tier) => t(`sales.tier.${tier}`),
    },
    ...CADENCE_KEYS.map((key): DataTableColumn<Tier> => {
      const header = t(CADENCE_COLUMN_KEYS[key]);
      return {
        key,
        header,
        render: (tier) => {
          const path = tierPath(tier, key);
          return (
            <>
              <input
                inputMode="numeric"
                value={form.tiers[tier][key]}
                readOnly={!canEdit}
                placeholder={t("sales.settings.inherit", { value: form[key] })}
                aria-label={`${t(`sales.tier.${tier}`)} — ${header}`}
                aria-invalid={errorFor(path) !== undefined ? true : undefined}
                data-testid={`sales-settings-tier-${tier}-${key}`}
                onChange={(e) => {
                  edit({
                    ...form,
                    tiers: {
                      ...form.tiers,
                      [tier]: { ...form.tiers[tier], [key]: e.target.value },
                    },
                  });
                }}
              />
              {errorLine(path)}
            </>
          );
        },
      };
    }),
  ];

  const nowMs = Date.now();
  // `?? null` is the D-230 deploy-skew guard: during a roll an older API task
  // answers the row WITHOUT the two stamps, and an absent stamp must read as
  // "not run yet", never as "ran less than a minute ago" off an undefined.
  const sweptAt = (row.engine_swept_at as string | null | undefined) ?? null;
  const activeSince = (row.reminders_active_since as string | null | undefined) ?? null;

  return (
    <section className="sales-settings-screen" data-testid="sales-settings-screen">
      <PageHeader
        title={t("sales.settings.title")}
        actions={
          <>
            {canEdit ? (
              <Button
                variant="primary"
                data-testid="sales-settings-save"
                busy={save.isPending}
                busyLabel={t("sales.settings.saving")}
                onClick={onSave}
              >
                {t("sales.settings.save")}
              </Button>
            ) : null}
          </>
        }
      />
      <SalesGroupTabs group="settings" />

      {canEdit ? null : (
        <p className="sales-pending-note" data-testid="sales-settings-read-only">
          {t("sales.settings.readOnlyNote")}
        </p>
      )}

      {saved ? (
        <p className="sales-hint" role="status" data-testid="sales-settings-saved">
          {t("sales.settings.saved")}
        </p>
      ) : null}
      {hasFieldErrors ? (
        <p className="sales-error-note" role="alert" data-testid="sales-settings-fix-fields">
          {t("sales.settings.fixFields")}
        </p>
      ) : null}
      {save.isError && Object.keys(serverErrors).length === 0 ? (
        <MutationFailureNote
          data-testid="sales-settings-save-failed"
          text={t("sales.settings.saveFailed")}
          error={save.error}
        />
      ) : null}
      {/* A background re-read that failed while the form is on the glass
          leaves the form readable — the next read replaces it. */}

      <Card
        className="sales-360-card"
        data-testid="sales-settings-engine"
        title={t("sales.settings.engineTitle")}
        action={
          <HelpTip
            label={t("nav.helpAbout", { topic: t("sales.settings.engineTitle") })}
            data-testid="sales-settings-engine-help"
          >
            {t("sales.settings.engineBody")}
          </HelpTip>
        }
      >
        {sweptAt === null ? (
          <p data-testid="sales-settings-engine-ran" data-swept-at="">
            {t("sales.settings.engineNotYet")}
          </p>
        ) : (
          <p data-testid="sales-settings-engine-ran" data-swept-at={sweptAt}>
            {t("sales.settings.engineLastRan", {
              ago: engineRanAgo(t, sweptAt, nowMs),
              when: formatTimestamp(sweptAt, i18n.language, timeZone),
            })}
          </p>
        )}
        {activeSince !== null ? (
          <p className="sales-hint" data-testid="sales-settings-active-since">
            {t("sales.settings.activeSince", {
              when: formatTimestamp(activeSince, i18n.language, timeZone),
            })}
          </p>
        ) : null}
      </Card>

      <Card
        className="sales-360-card"
        data-testid="sales-settings-ready"
        title={t("sales.settings.readyTitle")}
        action={
          <HelpTip
            label={t("nav.helpAbout", { topic: t("sales.settings.readyTitle") })}
            data-testid="sales-settings-ready-help"
          >
            {t("sales.settings.readyBody")}
          </HelpTip>
        }
      >
        <div className="sales-form">
          {numberField("ready_reminder_after_days", t("sales.settings.afterDays"))}
          {numberField("ready_reminder_max_sends", t("sales.settings.maxSends"))}
        </div>
        <p className="sales-hint">{t("sales.settings.offHint")}</p>
      </Card>

      <Card
        className="sales-360-card"
        data-testid="sales-settings-follow-up"
        title={t("sales.settings.followUpTitle")}
        action={
          <HelpTip
            label={t("nav.helpAbout", { topic: t("sales.settings.followUpTitle") })}
            data-testid="sales-settings-follow-up-help"
          >
            {t("sales.settings.followUpBody")}
          </HelpTip>
        }
      >
        <div className="sales-form">
          {numberField("quote_follow_up_after_days", t("sales.settings.afterDays"))}
          {numberField("quote_follow_up_max_sends", t("sales.settings.maxSends"))}
        </div>
        <p className="sales-hint">{t("sales.settings.offHint")}</p>
      </Card>

      <Card
        className="sales-360-card"
        data-testid="sales-settings-tiers"
        title={t("sales.settings.tiersTitle")}
      >
        <p className="sales-hint">{t("sales.settings.tiersBody")}</p>
        <DataTable
          data-testid="sales-settings-tier-table"
          columns={tierColumns}
          rows={TIERS}
          rowKey={(tier) => tier}
          empty={null}
          caption={t("sales.settings.tiersCaption")}
          rowTestId="sales-settings-tier-row"
          rowProps={(tier) => ({ "data-tier": tier })}
        />
      </Card>

      <Card
        className="sales-360-card"
        data-testid="sales-settings-quiet"
        title={t("sales.settings.quietTitle")}
        action={
          <HelpTip
            label={t("nav.helpAbout", { topic: t("sales.settings.quietTitle") })}
            data-testid="sales-settings-quiet-help"
          >
            {t("sales.settings.quietBody")}
          </HelpTip>
        }
      >
        <div className="sales-form">
          {(["quiet_hours_start", "quiet_hours_end"] as const).map((key) => (
            <Field
              key={key}
              label={
                key === "quiet_hours_start"
                  ? t("sales.settings.quietStart")
                  : t("sales.settings.quietEnd")
              }
            >
              <input
                type="time"
                value={form[key]}
                readOnly={!canEdit}
                aria-invalid={errorFor(key) !== undefined ? true : undefined}
                data-testid={`sales-settings-${key}`}
                onChange={(e) => {
                  edit({ ...form, [key]: e.target.value });
                }}
              />
              {errorLine(key)}
            </Field>
          ))}
        </div>
      </Card>

      <Card
        className="sales-360-card"
        data-testid="sales-settings-auto-confirm"
        title={t("sales.settings.autoConfirmTitle")}
        action={
          <HelpTip
            label={t("nav.helpAbout", { topic: t("sales.settings.autoConfirmTitle") })}
            data-testid="sales-settings-auto-confirm-help"
          >
            {t("sales.settings.autoConfirmBody")}
          </HelpTip>
        }
      >
        <div className="sales-form">
          {numberField("delivery_auto_confirm_days", t("sales.settings.autoConfirmDays"))}
        </div>
      </Card>

      <Card
        className="sales-360-card"
        data-testid="sales-settings-prices"
        title={t("sales.settings.pricesTitle")}
      >
        <CheckboxField
          label={t("sales.settings.trackingPrices")}
          checked={form.tracking_show_prices}
          disabled={!canEdit}
          data-testid="sales-settings-tracking-prices"
          onChange={(checked) => {
            edit({ ...form, tracking_show_prices: checked });
          }}
        />
        <CheckboxField
          label={t("sales.settings.deliveryNotePrices")}
          checked={form.delivery_note_show_prices}
          disabled={!canEdit}
          data-testid="sales-settings-delivery-note-prices"
          onChange={(checked) => {
            edit({ ...form, delivery_note_show_prices: checked });
          }}
        />
      </Card>

      {/* E2-1 (spec 09 §4.13, D-312 ruling 2): the pipeline's stages, with
          their own read and their own per-row save, independent of the
          settings row's PUT above. */}
      <PipelineStagesCard />

      {/* E2-2a1 (spec 09 §4.13, D-323 ruling 5): the tenant's tag list, with
          its own reads and writes, independent of the settings row's PUT. */}
      <TagsCard />
    </section>
  );
}
